import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import StructureImage from '../../components/chemistry/StructureImage';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

interface RDKitResult {
  smiles: string;
  count: number;
  descriptors: Record<string, number>;
}

const LIPINSKI = { MolWt: 500, MolLogP: 5, NumHDonors: 5, NumHAcceptors: 10 };
const VEBER = { TPSA: 140, NumRotatableBonds: 10 };

function checkViolations(descriptors: Record<string, number>): { name: string; value: number; limit: number; rule: string }[] {
  const violations: { name: string; value: number; limit: number; rule: string }[] = [];
  for (const [key, limit] of Object.entries(LIPINSKI)) {
    if (descriptors[key] !== undefined && descriptors[key] > limit) {
      violations.push({ name: key, value: descriptors[key], limit, rule: 'Lipinski' });
    }
  }
  for (const [key, limit] of Object.entries(VEBER)) {
    if (descriptors[key] !== undefined && descriptors[key] > limit) {
      violations.push({ name: key, value: descriptors[key], limit, rule: 'Veber' });
    }
  }
  return violations;
}

const WorkbenchRDKitDescriptors: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [inputText, setInputText] = useState(ctxSmiles || '');
  const [selectedExample, setSelectedExample] = useState('');
  const [filterText, setFilterText] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<RDKitResult | null>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const runCalculation = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await apiClient.post('/v2/descriptors/extended', {
        smiles: inputText.trim(),
      }, { signal: controller.signal });
      setResult(resp.data);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') setError('Calculation cancelled by user');
      else setError(err.response?.data?.detail || err.message || 'Calculation failed');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  const descriptorEntries = result
    ? Object.entries(result.descriptors).filter(([key]) =>
        !filterText.trim() || key.toLowerCase().includes(filterText.toLowerCase())
      )
    : [];

  const violations = result ? checkViolations(result.descriptors) : [];
  const violationKeys = new Set(violations.map((v) => v.name));

  return (
    <>
    <WorkbenchContextBar />
    <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="rdkit-descriptors" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: RDKit</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Calculation" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={h2Style}>RDKit Molecular Descriptors</h2>
            <p style={descStyle}>Compute ~200 RDKit descriptors for a molecule. Includes Lipinski and Veber violation highlighting.</p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { name: 'Aspirin', smi: 'CC(=O)Oc1ccccc1C(=O)O' },
                { name: 'Ibuprofen', smi: 'CC(C)Cc1ccc(C(C)C(=O)O)cc1' },
                { name: 'Caffeine', smi: 'Cn1c(=O)c2c(ncn2C)n(C)c1=O' },
              ].map((ex) => (
                <button key={ex.smi} onClick={() => { setInputText(ex.smi); setSelectedExample(ex.name); }}
                  style={{ ...btnStyle, ...(inputText === ex.smi ? { background: 'rgba(245,158,11,0.15)', borderColor: '#f59e0b', color: '#f59e0b' } : {}) }}>
                  {ex.name} <StructureImage smiles={ex.smi} size={24} />
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={inputText} onChange={(e) => { setInputText(e.target.value); setSelectedExample(''); }}
                placeholder="Enter molecule notation..."
                onKeyDown={(e) => { if (e.key === 'Enter' && inputText.trim()) runCalculation(); }}
                style={{ ...textareaStyle, height: 40, flex: 1 }} />
              <button onClick={runCalculation} disabled={!inputText.trim() || isRunning}
                style={{ ...primaryBtnStyle, opacity: inputText.trim() && !isRunning ? 1 : 0.5 }}>
                {isRunning ? 'Running...' : 'Calculate'}
              </button>
            </div>
            {selectedExample && inputText && (
              <div style={{ marginTop: -8, marginBottom: 8, color: 'var(--text-secondary)', fontSize: 12 }}>Selected: {selectedExample}</div>
            )}

            {isRunning && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={spinnerStyle} />
                <span style={{ color: '#f59e0b', fontSize: 14 }}>Computing RDKit descriptors...</span>
              </div>
            )}
            {error && <div style={errorStyle}>{error}</div>}

            {result && (
              <div>
                {violations.length > 0 && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <div style={{ color: '#fb7185', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                      Druglikeness Violations ({violations.length})
                    </div>
                    {violations.map((v) => (
                      <div key={v.name} style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 2 }}>
                        <span style={{ color: '#fb7185', fontFamily: 'monospace' }}>{v.name}</span>
                        {' = '}{v.value.toFixed(2)}{' '}
                        <span style={{ color: 'var(--text-secondary)' }}>(limit: {v.limit}, {v.rule})</span>
                      </div>
                    ))}
                  </div>
                )}
                {violations.length === 0 && (
                  <div style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <div style={{ color: '#34d399', fontSize: 13, fontWeight: 600 }}>
                      No Lipinski or Veber violations
                    </div>
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filter descriptors by name..."
                    style={{ ...textareaStyle, height: 36, maxWidth: 400, fontSize: 13 }} />
                </div>

                <div style={{ maxHeight: 500, overflow: 'auto', border: '1px solid var(--card-border)', borderRadius: 10, marginBottom: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--hover-bg)', zIndex: 1 }}>
                        <th style={thStyle}>Descriptor</th>
                        <th style={thStyle}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {descriptorEntries.map(([key, value]) => (
                        <tr key={key} style={{
                          borderBottom: '1px solid var(--card-border)',
                          background: violationKeys.has(key) ? 'rgba(239,68,68,0.06)' : undefined,
                        }}>
                          <td style={{ ...tdStyle, color: violationKeys.has(key) ? '#fb7185' : 'var(--text-primary)' }}>{key}</td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace', color: violationKeys.has(key) ? '#fb7185' : '#f59e0b' }}>
                            {typeof value === 'number' ? value.toFixed(4) : value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Showing {descriptorEntries.length} of {result.count} descriptors
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => {
                    const lines = ['descriptor,value', ...Object.entries(result.descriptors).map(([k, v]) => `${k},${v}`)];
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'rdkit_descriptors.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => {
                    const blob = new Blob([JSON.stringify(result.descriptors, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'rdkit_descriptors.json'; a.click(); URL.revokeObjectURL(url);
                  }} style={btnStyle}>Download JSON</button>
                  <button onClick={() => navigator.clipboard.writeText(JSON.stringify(result.descriptors, null, 2))} style={btnStyle}>Copy JSON</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="RDKit Descriptors" toolKey="rdkit-descriptors" smiles={ctxSmiles || inputText} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0 }}>
            <MethodCard engine="RDKit" title="RDKit Molecular Descriptors"
              description="Computes ~200 physicochemical and topological descriptors using the RDKit descriptor engine. Includes molecular weight, LogP, TPSA, H-bond donors/acceptors, rotatable bonds, ring counts, and many more."
              parameters={[
                { name: 'Lipinski', desc: 'MW < 500, LogP < 5, HBD < 5, HBA < 10' },
                { name: 'Veber', desc: 'TPSA < 140, RotBonds < 10' },
                { name: 'Output', desc: '~200 descriptors as name-value pairs' },
              ]}
              reference="Landrum (2006) RDKit: Open-Source Cheminformatics"
              referenceUrl="https://www.rdkit.org"
              source="RDKit Descriptors (local computation)" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 14, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--card-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 12px', color: 'var(--text-primary)' };

export default WorkbenchRDKitDescriptors;
